export interface RangeConfig<T = number | Date> {
  min?: T;
  max?: T;
  inclusive?: {
    min?: boolean;
    max?: boolean;
  };
}

export class Range<T = number | Date> {
  min?: T;
  max?: T;

  readonly inclusive = {
    min: true,
    max: false,
  };

  get isEmpty(): boolean {
    return !this.max && !this.min;
  }

  constructor(config?: RangeConfig<T> | string) {
    if (typeof config === 'string') {
      const result = /([\[\(])["]?([^"]*)["]?,["]?([^"]*)["]?([\]\)])/.exec(
        config
      );
      if (!result) {
        throw new Error('Invalid range');
      }
      const [, inclusiveMin, min, max, inclusiveMax] = result;
      config = {
        min: min ? this.parse(min) : undefined,
        max: max ? this.parse(max) : undefined,
        inclusive: {
          min: !min || inclusiveMin === '[',
          max: !max || inclusiveMax === ']',
        },
      };
    }

    if (config?.min) {
      this.min = config.min;
    }
    if (config?.max) {
      this.max = config.max;
    }
    if (config?.inclusive?.min !== undefined) {
      this.inclusive.min = config.inclusive.min;
    }
    if (config?.inclusive?.max !== undefined) {
      this.inclusive.max = config.inclusive.max;
    }
  }

  toString(): string {
    const minBound = this.inclusive.min ? '[' : '(';
    const maxBound = this.inclusive.max ? '[' : ')';
    const min = this.serialize(this.min);
    const max = this.serialize(this.max);

    if (!!min && !!max) {
      return `${minBound}${min},${max}${maxBound}`;
    } else if (!!min && !max) {
      return `${minBound}${min},)`;
    } else if (!min && !!max) {
      return `(,${max}${maxBound}`;
    } else {
      return '(,)';
    }
  }

  protected serialize(value: T | undefined): string | undefined {
    if (!value) {
      return undefined;
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    return String(value);
  }

  protected parse(value?: string): T | undefined {
    if (value === undefined) {
      return undefined;
    }
    const num = Number(value);
    return (isNaN(num) ? new Date(value) : num) as unknown as T;
  }
}
